// Dokumente eines Mieters hochladen und öffnen – eine Stelle für das Mietverhältnis-Formular der
// Einheit und das Dokumente-Panel des Mieters (Storage-Bucket „dokumente“, Texterkennung wie bisher).

import { supabase } from '../supabase/client';
import { speicherName } from '../storage/speicherName';
import { dokumentOeffnenUrl } from '../dokumente/dokumentLink';

export const DOK_MAX_BYTES = 25 * 1024 * 1024;
export const MIETER_DOK_SPALTEN = 'id, titel, datei_name, kategorie, datei_typ, datei_url, onedrive_url, gdrive_file_id, created_at, mieter_id, fuer_mieter_sichtbar';

export interface MieterDokument {
  id: string; titel: string | null; datei_name: string | null; kategorie: string | null; datei_typ: string | null; datei_url: string | null;
  onedrive_url: string | null; gdrive_file_id: string | null; created_at: string; mieter_id?: string | null;
  /** Mieterportal (Portale E5): für den Mieter freigegeben */
  fuer_mieter_sichtbar?: boolean;
}

const ABSOLUT = /^https?:\/\//i;

export async function ladeMieterDokumentHoch(p: {
  datei: File; mieterId: string; mieterName: string; objektId: string | null; kategorie: string; ordner: string;
  /** Dokument gehört einem Eigentümer statt einem Mieter (eigentuemer_id statt mieter_id) */
  eigentuemer?: boolean;
  /** Dokument gehört einem Dienstleister (dienstleister_id) */
  dienstleister?: boolean;
}): Promise<MieterDokument> {
  if (p.datei.size > DOK_MAX_BYTES) throw new Error('Die Datei ist zu groß (max. 25 MB)');
  const pfad = `${p.ordner}/${p.mieterId}/${Date.now()}_${speicherName(p.datei.name)}`;
  const up = await supabase.storage.from('dokumente').upload(pfad, p.datei);
  if (up.error) throw new Error(up.error.message);
  const { data, error } = await supabase.from('dokumente').insert({
    titel: p.datei.name.replace(/\.[^/.]+$/, ''), datei_name: p.datei.name, datei_url: pfad, datei_groesse: p.datei.size,
    datei_typ: p.datei.name.split('.').pop()?.toLowerCase() || null, kategorie: p.kategorie,
    // assigned_to kennt (Check) nur objekt/mieter/eigentuemer – Dienstleister-Dokumente hängen an dienstleister_id
    ...(p.dienstleister ? { dienstleister_id: p.mieterId, assigned_to: null }
      : p.eigentuemer ? { eigentuemer_id: p.mieterId, assigned_to: 'eigentuemer' } : { mieter_id: p.mieterId, assigned_to: 'mieter' }),
    objekt_id: p.objektId, assigned_name: p.mieterName, status: 'Neu', is_deleted: false,
  }).select(MIETER_DOK_SPALTEN).single();
  if (error) throw new Error(error.message);
  const dok = data as MieterDokument;
  // Texterkennung im Hintergrund (wie bisher)
  supabase.functions.invoke('dokument-text-extract', { body: { dokument_id: dok.id } }).catch(err => console.warn('Text-Extraktion fehlgeschlagen:', err));
  return dok;
}

/** Datei öffnen: Storage-Pfad über signierte URL, sonst der gespeicherte Link. false = nicht erreichbar. */
export async function oeffneMieterDokument(d: MieterDokument, download = false): Promise<boolean> {
  const pfad = (d.datei_url ?? '').trim();
  if (pfad && !ABSOLUT.test(pfad)) {
    const { data } = await supabase.storage.from('dokumente').createSignedUrl(pfad, 3600, download ? { download: true } : undefined);
    if (!data?.signedUrl) return false;
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    return true;
  }
  const url = dokumentOeffnenUrl(d);
  if (!url) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}
